import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { Game } from '../services/backendService/types';
import {
  Template,
  TemplateEditOrCreateDTO,
} from '../services/TemplateService/Template.type';
import { gameCacheKey } from './useGame';
import { workspaceTemplatesCacheKey } from './useWorkspaceTemplates';

const useCreateGameFromTemplate = () => {
  const client = useQueryClient();
  const {
    mutate: createGameFromTemplate,
    isPending: isCreatingGameFromTemplate,
    isSuccess: isCreatingGameFromTemplateSuccess,
  } = useMutation({
    mutationKey: ['createGameFromTemplate'],
    mutationFn: async ({
      templateConfig,
      activeTenantId,
      isFromDiscover,
    }: {
      templateConfig: TemplateEditOrCreateDTO;
      activeTenantId?: string;
      onSuccessFunction?: (data: string) => void | undefined;
      isFromDiscover?: boolean;
    }) => {
      if (!templateConfig.id) return Promise.resolve(undefined);

      return BackendService.instance.template.createGameFromTemplate({
        templateId: templateConfig.id,
        slides: templateConfig.slides || [],
        title: templateConfig.title,
        isFromDiscover,
        requireRealNames:
          templateConfig.requireRealNames === undefined ||
          templateConfig.requireRealNames === null
            ? true
            : templateConfig.requireRealNames,
        isContentFilterEnabled:
          templateConfig.isContentFilterEnabled === undefined ||
          templateConfig.isContentFilterEnabled === null
            ? true
            : templateConfig.isContentFilterEnabled,
        activeTenantId,
        metadata: templateConfig.metadata,
        currentRound: templateConfig.currentRound,
        attachments: templateConfig.attachments,
        settings: templateConfig.settings || undefined,
      });
    },
    onSuccess: (data, { onSuccessFunction, templateConfig }) => {
      if (!data) return;
      const previousTemplate = client.getQueryData<Template>([
        'template',
        templateConfig.id,
      ]);
      client.setQueryData<Template | undefined>(['template', templateConfig.id], () => {
        if (!previousTemplate) return undefined;
        return {
          ...previousTemplate,
          content: {
            ...previousTemplate?.content,
            activeGame: { isOngoing: false, id: data?.id },
          },
        };
      });

      client.invalidateQueries({
        queryKey: [workspaceTemplatesCacheKey],
      });

      client.setQueryData<Game>([gameCacheKey, data.id], data);
      if (onSuccessFunction) {
        onSuccessFunction(data.id);
      }
    },
  });

  return {
    createGameFromTemplate,
    isCreatingGameFromTemplate,
    isCreatingGameFromTemplateSuccess,
  };
};

export default useCreateGameFromTemplate;
